import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTarefas } from '@/api/queries/tarefas';
import { useTarefaAction, type TarefaAction } from '@/api/mutations/tarefa';
import { Skeleton } from '@/components/ui/skeleton';
import { Check, Clock, AlertTriangle, RotateCcw, ChevronDown, Inbox } from 'lucide-react';
import { cn } from '@/lib/cn';
import { toast } from 'sonner';
import type { Tarefa } from '@/api/types';

const tempDot: Record<string, string> = {
  frio: 'bg-[color:var(--color-temp-frio)]',
  morno: 'bg-[color:var(--color-temp-morno)]',
  quente: 'bg-[color:var(--color-temp-quente)]',
};

const energiaSymbol: Record<string, string> = {
  alta: '⏶',
  media: '◐',
  baixa: '○',
};

function formatVencimento(dataVenc: string, horas?: number) {
  const date = new Date(dataVenc);
  const label = date.toLocaleString('pt-BR', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
  });
  if (horas === undefined) return label;
  if (horas < 0) return `${Math.abs(Math.round(horas))}h atrás`;
  if (horas < 1) return 'agora';
  if (horas < 24) return `em ${Math.round(horas)}h`;
  return `em ${Math.round(horas / 24)}d`;
}

interface TarefaRowProps {
  t: Tarefa;
  focused?: boolean;
  onFocus?: () => void;
  onExec: (a: TarefaAction) => void;
}

function TarefaRow({ t, focused, onFocus, onExec }: TarefaRowProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const isConcluida = t.status === 'concluida';
  const isCancelada = t.status === 'cancelada';
  const estourou = t.sla_estourou;

  return (
    <div
      onMouseEnter={onFocus}
      className={cn(
        'group grid grid-cols-[auto_1fr_auto] items-center gap-3 border-b border-border/50 px-2 py-2.5 transition-colors',
        focused && 'bg-muted/60',
        !focused && 'hover:bg-muted/40',
        isConcluida && 'opacity-40',
      )}
    >
      <button
        type="button"
        onClick={() => onExec(isConcluida ? 'reabrir' : 'concluir')}
        className={cn(
          'inline-flex h-5 w-5 items-center justify-center rounded-full border transition-colors',
          isConcluida
            ? 'border-primary bg-primary text-primary-foreground'
            : 'border-muted-foreground/40 hover:border-primary',
        )}
        aria-label={isConcluida ? 'Reabrir' : 'Concluir'}
      >
        {isConcluida && <Check className="h-3 w-3" strokeWidth={3} />}
      </button>

      <div className="min-w-0 flex items-center gap-2">
        {t.empresa_temperatura && (
          <span className={cn('h-1.5 w-1.5 rounded-full shrink-0', tempDot[t.empresa_temperatura])} />
        )}
        <Link
          to={`/empresas/${t.empresa_id}`}
          className="shrink-0 text-[13px] font-medium hover:underline truncate max-w-[200px]"
        >
          {t.empresa_nome}
        </Link>
        <span className="text-muted-foreground/40">·</span>
        <span className="text-[11px] uppercase tracking-wide text-muted-foreground/80 mono shrink-0">
          {t.tipo.replace(/_/g, ' ')}
        </span>
        {t.descricao_livre && (
          <>
            <span className="text-muted-foreground/40">·</span>
            <span className={cn('truncate text-[13px] text-foreground/85', isConcluida && 'line-through')}>
              {t.descricao_livre}
            </span>
          </>
        )}
      </div>

      <div className="flex items-center gap-3 shrink-0">
        <span
          className={cn(
            'inline-flex items-center gap-1 text-[11px] tabular',
            estourou ? 'text-destructive font-medium' : 'text-muted-foreground',
          )}
        >
          {estourou ? (
            <AlertTriangle className="h-3 w-3" strokeWidth={2.25} />
          ) : (
            <Clock className="h-3 w-3" strokeWidth={2.25} />
          )}
          {formatVencimento(t.data_vencimento, t.horas_pro_vencimento)}
        </span>
        <span className="text-[11px] text-muted-foreground/70 mono shrink-0 w-3 text-center" title={`energia ${t.energia_requerida}`}>
          {energiaSymbol[t.energia_requerida]}
        </span>
        {t.prioridade === 'alta' && (
          <span className="text-[11px] font-semibold text-destructive mono">!</span>
        )}

        {!isConcluida && !isCancelada && (
          <div className={cn('relative transition-opacity', focused || menuOpen ? 'opacity-100' : 'opacity-0 group-hover:opacity-100')}>
            <button
              type="button"
              onClick={() => setMenuOpen((o) => !o)}
              className="inline-flex items-center gap-0.5 rounded border border-border/70 px-1.5 py-0.5 text-[11px] transition-colors hover:bg-muted"
            >
              adiar
              <ChevronDown className="h-3 w-3" strokeWidth={2.25} />
            </button>
            {menuOpen && (
              <div className="absolute right-0 top-full z-30 mt-1 min-w-28 rounded-md border border-border/70 bg-popover p-1 shadow-lg">
                <button type="button" onClick={() => { setMenuOpen(false); onExec('adiar_1h'); }} className="block w-full rounded-sm px-2 py-1 text-left text-[11px] hover:bg-muted">+1 hora</button>
                <button type="button" onClick={() => { setMenuOpen(false); onExec('adiar_1d'); }} className="block w-full rounded-sm px-2 py-1 text-left text-[11px] hover:bg-muted">+1 dia</button>
                <button type="button" onClick={() => { setMenuOpen(false); onExec('adiar_7d'); }} className="block w-full rounded-sm px-2 py-1 text-left text-[11px] hover:bg-muted">+1 semana</button>
                <div className="my-1 border-t border-border/50" />
                <button type="button" onClick={() => { setMenuOpen(false); onExec('cancelar'); }} className="block w-full rounded-sm px-2 py-1 text-left text-[11px] text-destructive hover:bg-destructive/10">cancelar</button>
              </div>
            )}
          </div>
        )}
        {isCancelada && (
          <button
            type="button"
            onClick={() => onExec('reabrir')}
            className="inline-flex items-center gap-1 rounded border border-border/70 px-1.5 py-0.5 text-[11px] hover:bg-muted"
          >
            <RotateCcw className="h-3 w-3" />
            reabrir
          </button>
        )}
      </div>
    </div>
  );
}

export function InboxPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const filter = (searchParams.get('filter') ?? 'pendentes') as 'pendentes' | 'estouradas' | 'todas';
  const nav = useNavigate();
  const action = useTarefaAction();
  const [focusIdx, setFocusIdx] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  function setFilter(f: 'pendentes' | 'estouradas' | 'todas') {
    const next = new URLSearchParams(searchParams);
    if (f === 'pendentes') next.delete('filter');
    else next.set('filter', f);
    setSearchParams(next, { replace: true });
  }

  const { data, isLoading } = useTarefas({
    apenas_pendentes: filter === 'pendentes' || filter === 'estouradas',
    com_sla_estouro: filter === 'estouradas',
    limit: 500,
  });

  const list = data ?? [];

  async function exec(idx: number, a: TarefaAction) {
    const t = list[idx];
    if (!t) return;
    try {
      await action.mutateAsync({ id: t.id, action: a });
    } catch (e) {
      toast.error('Falha: ' + (e instanceof Error ? e.message : 'erro'));
    }
  }

  // Keyboard shortcuts
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      // ignore when typing in input
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (list.length === 0) return;

      switch (e.key) {
        case 'j':
        case 'ArrowDown':
          e.preventDefault();
          setFocusIdx((i) => Math.min(i + 1, list.length - 1));
          break;
        case 'k':
        case 'ArrowUp':
          e.preventDefault();
          setFocusIdx((i) => Math.max(i - 1, 0));
          break;
        case 'Enter': {
          e.preventDefault();
          const t = list[focusIdx];
          if (t) nav(`/empresas/${t.empresa_id}`);
          break;
        }
        case 'x':
        case ' ': {
          e.preventDefault();
          const t = list[focusIdx];
          if (t) exec(focusIdx, t.status === 'concluida' ? 'reabrir' : 'concluir');
          break;
        }
        case 'e':
          e.preventDefault();
          exec(focusIdx, 'adiar_1d');
          break;
        case 'g':
          e.preventDefault();
          setFocusIdx(0);
          break;
        case 'G':
          e.preventDefault();
          setFocusIdx(list.length - 1);
          break;
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list, focusIdx, nav]);

  useEffect(() => {
    if (!containerRef.current) return;
    const el = containerRef.current.children[focusIdx];
    if (el && 'scrollIntoView' in el) {
      (el as HTMLElement).scrollIntoView({ block: 'nearest' });
    }
  }, [focusIdx]);

  useEffect(() => {
    setFocusIdx(0);
  }, [filter, data?.length]);

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between border-b border-border/70 pb-3">
        <div>
          <h1 className="text-[28px] font-semibold tracking-[-0.02em] leading-tight">Inbox</h1>
          <p className="mt-0.5 text-xs text-muted-foreground tabular">
            <span className="mono">{list.length}</span>{' '}
            {filter === 'estouradas'
              ? 'tarefas com SLA estourado'
              : filter === 'pendentes'
                ? 'tarefas pendentes'
                : 'tarefas no total'}
            <span className="ml-3 text-muted-foreground/60">
              <kbd className="mono rounded border border-border/70 px-1 text-[10px]">j</kbd>
              <kbd className="mono rounded border border-border/70 px-1 text-[10px] ml-1">k</kbd>
              <span className="ml-1">navegar</span>
              <kbd className="mono rounded border border-border/70 px-1 text-[10px] ml-2">x</kbd>
              <span className="ml-1">concluir</span>
              <kbd className="mono rounded border border-border/70 px-1 text-[10px] ml-2">e</kbd>
              <span className="ml-1">adiar 1d</span>
              <kbd className="mono rounded border border-border/70 px-1 text-[10px] ml-2">↵</kbd>
              <span className="ml-1">abrir</span>
            </span>
          </p>
        </div>
        <div className="flex items-center gap-4 text-xs">
          {(
            [
              ['pendentes', 'Pendentes'],
              ['estouradas', 'Estouradas'],
              ['todas', 'Todas'],
            ] as const
          ).map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setFilter(key)}
              className={cn(
                'border-b-2 pb-2 transition-colors',
                filter === key
                  ? 'border-primary text-foreground font-medium'
                  : 'border-transparent text-muted-foreground hover:text-foreground',
              )}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : list.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-border py-16 text-center">
          <Inbox className="h-8 w-8 text-muted-foreground/40" strokeWidth={1.5} />
          <p className="text-sm text-muted-foreground">Nada na inbox.</p>
        </div>
      ) : (
        <div ref={containerRef} className="rounded-md border border-border/70 bg-card">
          {list.map((t, idx) => (
            <TarefaRow
              key={t.id}
              t={t}
              focused={idx === focusIdx}
              onFocus={() => setFocusIdx(idx)}
              onExec={(a) => exec(idx, a)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
